import React, { useEffect, useState } from "react";
import Navbar from "../../components/layout/navbar";
import Tab from "../../components/tab";
import useRequest from "../../components/hooks/use-request";
import UserTable from "./user-table";
import PermissionTable from "./permission-table";
import Button from "../../components/button";
import { Plus } from "lucide-react";
import AddUser from "./user-modal/add-user";
import CreateRole from "./user-modal/create-role";

interface Permissions {
  canViewReports: boolean;
  canManageUsers: boolean;
  canManageInventory: boolean;
  canManageSales: boolean;
  canManageSupport: boolean;
  [key: string]: boolean;
}

interface UserData {
  _id: string;
  email: string;
  invitedBy: string;
  isActive: string;
  permissions: Permissions;
  role: string;
  createdAt: string;
  lastLogin: string;
  username: string;
  whatsappNo: string;
  updatedAt: string;
  [key: string]: string | undefined | Permissions;
}

interface ProfileData {
  username: string;
  email: string;
  phone: string;
  address: string;
  lastLogin: string;
  role: string;
}

// In src/pages/users/index.tsx
interface Permission {
  resource: string;
  actions: string[];
  _id: string;
}

interface RolesData {
  _id: string;
  createdAt: string;
  updatedAt: string;
  permissions: Permission[];
  name: string;
  isActive?: string;
  [key: string]: string | undefined | Permission[];
}

const Users = () => {
  const { loading, makeRequest: getUsers } = useRequest("/users", "GET");
  const { makeRequest: getRoles } = useRequest("/roles", "GET");
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [userType, setUserType] = useState("Users");
  const [users, setUsers] = useState<UserData[]>([]);
  const [roles, setRoles] = useState<RolesData[]>([]);
  const [totalPages, setTotalPages] = useState([]);
  const [userModal, setAddUserModal] = useState(false);
  const [roleModal, setCloseRoleModal] = useState(false);
  const userToken = localStorage.getItem("token");
  const { makeRequest: getProfile } = useRequest("/users/profile", "GET", {
    Authorization: `Bearer ${userToken}`,
  });
  async function fetchUserData() {
    const [response] = await getUsers(undefined, {});
    setUsers(response?.data?.users || []);
    // setTotalPages(Math.ceil(response.data?.totalPages));
  }

  useEffect(() => {
    fetchUserData();
  }, []);

  async function fetchRolesData() {
    const [response] = await getRoles(undefined, {});
    setRoles(response?.data?.roles || []);
    // setTotalPages(Math.ceil(response.data?.totalPages));
  }

  useEffect(() => {
    fetchRolesData();
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      const [response] = await getProfile();
      if (response.status) {
        setProfile(response.data?.user);
      }
    };
    fetchProfile();
  }, []);

  function closeUserModal() {
    setAddUserModal(false);
  }

  function openUserModal() {
    setAddUserModal(true);
  }

  function closeRoleModal() {
    setCloseRoleModal(false);
  }

  function openRoleModal() {
    setCloseRoleModal(true);
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar profile={profile as ProfileData} />
        <main className="flex-1 container mx-auto py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-[30px] font-extrabold text-black">
                Users & Permissions
              </h1>
              <p className="text-[#676767] text-[18px] font-normal">
                Create users and team members and add roles or permissions to
                them.
              </p>
            </div>
            <div className="md:flex md:w-[25%] gap-2">
              <Button
                size="lg"
                variant="secondary"
                type="button"
                className="font-normal"
                onClick={openRoleModal}
              >
                Create Role
              </Button>

              <Button
                size="lg"
                variant="primary"
                type="button"
                className="font-normal md:mt-0 mt-6"
                onClick={openUserModal}
              >
                <Plus /> Add User
              </Button>
            </div>
          </div>

          <div className="flex flex-col justify-center items-center">
            <Tab
              activeTab={userType}
              tabs={["Users", "Roles"]}
              setActiveTab={setUserType}
            />
          </div>
          {userType === "Users" && <UserTable data={users} />}

          {userType === "Roles" && <PermissionTable data={roles} />}
        </main>
      </div>
      <>
        {profile?.role === "superAdmin" && (
          <AddUser
            isVisible={userModal}
            onClose={closeUserModal}
            roles={roles}
          />
        )}
      </>

      <>
        {profile?.role === "superAdmin" && (
          <CreateRole isVisible={roleModal} onClose={closeRoleModal} />
        )}
      </>
    </>
  );
};

export default Users;
